import { useFormControls } from '@mews/framework';
import { UseSteppedFormControlsParams } from '../types';

export const useSteppedFormControls = <S extends string, I extends object, O extends object = I>({
    steps,
    stepHandlers,
    stepResolvers,
    currentStep,
    setCurrentStep,
    onSubmit,
    ...other
}: UseSteppedFormControlsParams<S, I, O>) => {
    const { renderFormContent, ...formControls } = useFormControls<I, O>({
        ...other,
        resolver: stepResolvers?.[currentStep],
        onSubmit: async (formValues, extra) => {
            const currentStepIndex = steps.indexOf(currentStep);
            if (currentStepIndex < steps.length - 1) {
                if (stepHandlers?.[currentStep]) {
                    await stepHandlers[currentStep](formValues, { setCurrentStep });
                } else {
                    setCurrentStep(steps[currentStepIndex + 1]);
                }
            } else {
                await onSubmit(formValues, extra);
            }
        },
    });

    type FormContent = ReturnType<typeof renderFormContent>;
    type StepMatcher = (matchingMap: Record<S, () => FormContent>) => FormContent;

    return {
        ...formControls,
        renderFormContent: (render: (props: { matchSteps: StepMatcher }) => FormContent) =>
            render({
                matchSteps: (matchingMap) => renderFormContent(matchingMap[currentStep]()),
            }),
    };
};
